/**
 * Price Labels Configuration
 * 
 * Maps subcategory IDs to contextual pricing labels.
 * This allows the same form to show different labels based on the business context.
 * 
 * Example: "Price" becomes "Session Fee" for wellness services,
 *          "Rental Rate" for equipment rentals, etc.
 */

const priceLabels = {
  // Service Subcategories
  'salon-spa': {
    price: 'Service Fee',
    priceHelper: 'Base price for this salon/spa service',
    comparePrice: 'Regular Price',
    comparePriceHelper: 'Show as struck-through for discounts'
  },
  'wellness-fitness': {
    price: 'Session Fee',
    priceHelper: 'Fee per session or class',
    comparePrice: 'Regular Fee',
    comparePriceHelper: 'Original price before discount'
  },
  'photography': {
    price: 'Starting Price',
    priceHelper: 'Base package price',
    comparePrice: 'Standard Price',
    comparePriceHelper: 'Show original price for promotional offers'
  },
  'home-services': {
    price: 'Service Charge',
    priceHelper: 'Base fee for this service (materials extra)',
    comparePrice: 'Standard Rate',
    comparePriceHelper: 'Regular pricing'
  },
  'consulting': {
    price: 'Consultation Fee',
    priceHelper: 'Fee per consultation or hour',
    comparePrice: 'Standard Fee',
    comparePriceHelper: 'Regular consultation rate'
  },
  'education-tutoring': {
    price: 'Tuition Fee',
    priceHelper: 'Fee per class or session',
    comparePrice: 'Regular Fee',
    comparePriceHelper: 'Standard tuition rate'
  },
  
  // Rental Subcategories
  'camera-equipment': {
    price: 'Daily Rental',
    priceHelper: 'Rental rate per day',
    comparePrice: 'Standard Rate',
    comparePriceHelper: 'Regular daily rate'
  },
  'vehicles': {
    price: 'Rental Rate',
    priceHelper: 'Base rental rate per day',
    comparePrice: 'Standard Rate',
    comparePriceHelper: 'Regular rental rate'
  },
  'party-supplies': {
    price: 'Rental Fee',
    priceHelper: 'Rental fee for the event duration',
    comparePrice: 'Standard Fee',
    comparePriceHelper: 'Regular rental fee'
  },
  'venue-spaces': {
    price: 'Venue Fee',
    priceHelper: 'Fee per hour or event',
    comparePrice: 'Standard Rate',
    comparePriceHelper: 'Regular venue rate'
  },
  'sound-system': {
    price: 'Equipment Rental',
    priceHelper: 'Rental fee per event/day',
    comparePrice: 'Standard Rate',
    comparePriceHelper: 'Regular rental rate'
  },
  
  // Event Subcategories
  'workshop': {
    price: 'Registration Fee',
    priceHelper: 'Per-person registration fee',
    comparePrice: 'Early Bird Price',
    comparePriceHelper: 'Discounted early registration'
  },
  'concert': {
    price: 'Ticket Price',
    priceHelper: 'Base ticket price',
    comparePrice: 'Regular Price',
    comparePriceHelper: 'Standard ticket price'
  },
  'class': {
    price: 'Class Fee',
    priceHelper: 'Fee per class or series',
    comparePrice: 'Regular Fee',
    comparePriceHelper: 'Standard class fee'
  },
  
  // Product defaults based on listing type
  'product': {
    price: 'Price',
    priceHelper: 'Selling price for this product',
    comparePrice: 'Compare at Price',
    comparePriceHelper: 'Original price to show savings (optional)'
  },
  
  // Default fallback
  'default': {
    price: 'Price',
    priceHelper: 'Base price',
    comparePrice: 'Compare at Price',
    comparePriceHelper: 'Original price to show discount (optional)'
  }
};

/**
 * Get contextual price labels based on subcategory or listing type
 * 
 * @param {string} subcategoryId - The subcategory ID (e.g., 'salon-spa')
 * @param {string} listingType - Fallback listing type (e.g., 'service', 'rental')
 * @returns {object} Labels object with price, priceHelper, comparePrice, comparePriceHelper
 */
export const getPriceLabels = (subcategoryId, listingType = 'product') => {
  // First try exact subcategory match
  if (subcategoryId && priceLabels[subcategoryId]) {
    return priceLabels[subcategoryId];
  }
  
  // Fall back to listing type defaults
  const listingTypeDefaults = {
    service: {
      price: 'Service Fee',
      priceHelper: 'Base price for this service',
      comparePrice: 'Regular Price',
      comparePriceHelper: 'Show original price for discounts'
    },
    rental: {
      price: 'Rental Rate',
      priceHelper: 'Base rental rate',
      comparePrice: 'Standard Rate',
      comparePriceHelper: 'Regular rental rate'
    },
    event: {
      price: 'Ticket Price',
      priceHelper: 'Base ticket or registration price',
      comparePrice: 'Regular Price',
      comparePriceHelper: 'Standard ticket price'
    },
    subscription: {
      price: 'Subscription Price',
      priceHelper: 'Price per billing cycle',
      comparePrice: 'Regular Price',
      comparePriceHelper: 'Standard subscription price'
    }
  };
  
  if (listingType && listingTypeDefaults[listingType]) {
    return listingTypeDefaults[listingType];
  }
  
  return priceLabels['default'];
};

/**
 * Get all available label configurations (for admin/debugging)
 */
export const getAllPriceLabels = () => priceLabels;

export default priceLabels;
